import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  FlatList,
  TouchableOpacity,
  Alert,
  Modal,
  TextInput,
  ScrollView,
  Platform
} from 'react-native';
import AdminLayout from '../components/layout/AdminLayout';
import { Ionicons } from '@expo/vector-icons';
import { getApiUrl } from '../../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { H1, H2, Body1, Body2, Caption } from '../../components/common/TextStyles';

const apiCall = async (endpoint, options = {}) => {
  const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
  const response = await fetch(`${getApiUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'API Request Failed');
  }
  return response.json();
};

const HoneyPotManagementScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'questions'

  // Create Form State
  const [formData, setFormData] = useState({
    title: '',
    topic: '',
    difficulty: 'medium',
    questionCount: '10',
    passingScore: '70',
    bundleRewardAmount: '50',
    bundleRewardLabel: '50MB Data',
    durationMinutes: '60',
    lifetimeHours: '24',
    rewardType: 'mbs'
  });

  // Questions State
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  const [selectedChallenge, setSelectedChallenge] = useState(null);

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async (isPolling = false) => {
    try {
      if (!isPolling) setLoading(true);
      // Add timestamp to prevent caching during polling
      const data = await apiCall(`/honeypot/admin/challenges?t=${Date.now()}`);
      if (data) setChallenges(data);
    } catch (err) {
      console.error(err);
      if (!isPolling) Alert.alert('Error', 'Failed to load challenges');
    } finally {
      if (!isPolling) setLoading(false);
    }
  };

  // Poll for status updates if any challenge is 'generating'
  useEffect(() => {
    const hasGenerating = challenges.some(c => c.status === 'generating');
    let interval;

    if (hasGenerating) {
      interval = setInterval(() => {
        fetchChallenges(true);
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [challenges]);

  const handleCreate = async () => {
    try {
      setLoading(true);
      // Ensure numerical values
      const payload = {
        ...formData,
        questionCount: parseInt(formData.questionCount, 10),
        passingScore: parseInt(formData.passingScore, 10),
        bundleRewardAmount: parseFloat(formData.bundleRewardAmount),
        durationMinutes: parseInt(formData.durationMinutes, 10),
        lifetimeHours: parseInt(formData.lifetimeHours, 10),
        rewardType: formData.rewardType
      };

      await apiCall('/honeypot/admin/challenges', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setShowModal(false);
      Alert.alert('Success', 'Challenge record created. AI is currently generating trivia questions in the background. Please wait a moment and refresh to review them.');
      fetchChallenges();
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to create challenge');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    console.log('Delete button pressed for ID:', id);

    if (Platform.OS === 'web') {
      const confirmed = window.confirm('Are you sure you want to permanently delete this challenge? All associated questions and attempts will be lost.');
      if (confirmed) {
        performDelete(id);
      }
      return;
    }

    Alert.alert(
      'Delete Challenge',
      'Are you sure you want to permanently delete this challenge? All associated questions and attempts will be lost.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => performDelete(id)
        }
      ]
    );
  };

  const performDelete = async (id) => {
    try {
      console.log('Executing delete for:', id);
      setLoading(true);
      await apiCall(`/honeypot/admin/challenges/${id}`, { method: 'DELETE' });
      console.log('Delete successful');
      fetchChallenges(); // Refresh list
    } catch (err) {
      console.error('Delete failed:', err);
      Alert.alert('Error', 'Failed to delete challenge: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const activateChallenge = async (id) => {
    try {
      setLoading(true);
      await apiCall(`/honeypot/admin/challenges/${id}/activate`, { method: 'PUT' });
      fetchChallenges();
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to activate challenge');
      setLoading(false);
    }
  };

  const endChallenge = async (id) => {
    try {
      setLoading(true);
      await apiCall(`/honeypot/admin/challenges/${id}/end`, { method: 'PUT' });
      fetchChallenges();
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to end challenge');
      setLoading(false);
    }
  };

  const handleManualReward = async (attemptId) => {
    try {
      setLoading(true);
      await apiCall(`/honeypot/admin/attempts/${attemptId}/reward`, { method: 'POST' });
      Alert.alert('Success', 'Reward manually awarded.');
    } catch (err) {
      Alert.alert('Error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReward = async () => {
    const passers = attempts.filter(a => a.passed && !a.bundleAwarded);
    if (passers.length === 0) {
      return Alert.alert('Info', 'No pending passers to reward.');
    }

    Alert.alert(
      'Bulk Reward',
      `Are you sure you want to reward ${passers.length} users at once?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Proceed',
          onPress: async () => {
            try {
              setLoading(true);
              const res = await apiCall(`/honeypot/admin/challenges/${selectedChallenge.id}/bulk-reward`, { method: 'POST' });
              Alert.alert('Success', res.message);
              viewAttempts(selectedChallenge);
            } catch (err) {
              Alert.alert('Error', 'Bulk reward failed');
            } finally {
              setLoading(false);
            }
          }
        }
      ]
    );
  };

  const viewAttempts = (challenge) => {
    // Navigate to full-screen attempts page instead of modal
    navigation.navigate('HoneyPotAttempts', { challenge });
  };

  const reviewQuestions = async (challenge) => {
    setSelectedChallenge(challenge);
    setModalMode('questions');
    setShowModal(true);
    try {
      setQuestionsLoading(true);
      const data = await apiCall(`/honeypot/admin/challenges/${challenge.id}/questions`);
      if (data) setQuestions(data);
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to load questions');
    } finally {
      setQuestionsLoading(false);
    }
  };

  const saveQuestions = async () => {
    try {
      setQuestionsLoading(true);
      await apiCall(`/honeypot/admin/challenges/${selectedChallenge.id}/questions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions })
      });
      Alert.alert('Success', 'Questions updated successfully.');
      setShowModal(false);
      fetchChallenges(); // Refresh the list to reflect updates
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Failed to save questions');
    } finally {
      setQuestionsLoading(false);
    }
  };

  const renderChallengeItem = ({ item }) => {
    let statusColor = '#9CA3AF';
    if (item.status === 'active') statusColor = '#059669';
    if (item.status === 'ended') statusColor = '#EF4444';
    if (item.status === 'generating') statusColor = '#F59E0B'; // Orange
    if (item.status === 'failed') statusColor = '#DC2626'; // Bright Red

    return (
      <TouchableOpacity
        style={styles.compactCard}
        onPress={() => viewAttempts(item)}
        activeOpacity={0.7}
      >
        <View style={styles.cardInfo}>
          <View style={{flexDirection: 'row', alignItems: 'center'}}>
            <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
            <Caption style={{ color: statusColor, fontWeight: '700', marginRight: 8, fontSize: 10 }}>
              {(item.status || 'draft').toUpperCase()}
            </Caption>
            <Text style={styles.compactTitle} numberOfLines={1}>{item.title}</Text>
          </View>
          <View style={styles.compactMetaRow}>
            <Caption style={styles.metaLabel}>{item.topic}</Caption>
            <Caption style={styles.metaDivider}>•</Caption>
            <Caption style={styles.metaLabel}>{item.bundleRewardLabel}</Caption>
            <Caption style={styles.metaDivider}>•</Caption>
            <Caption style={styles.metaLabel}>{item.totalAttempts} att.</Caption>
          </View>
        </View>

        <View style={styles.compactActions}>
          <TouchableOpacity
            onPress={() => handleDelete(item.id)}
            style={[styles.iconBtn, { backgroundColor: '#FEE2E2' }]}
            hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
          >
            <Ionicons name="trash-outline" size={18} color="#EF4444" />
          </TouchableOpacity>

          {item.status === 'generating' ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              <ActivityIndicator size="small" color="#F59E0B" />
              <Caption style={{ color: '#F59E0B', fontWeight: 'bold' }}>AI GEN...</Caption>
            </View>
          ) : item.status === 'failed' ? (
            <TouchableOpacity onPress={() => reviewQuestions(item)} style={[styles.statusBtn, { backgroundColor: '#FEF2F2' }]}>
              <Caption style={{ color: '#DC2626', fontWeight: 'bold', fontSize: 10 }}>AI FAILED - RETRY</Caption>
            </TouchableOpacity>
          ) : item.status === 'draft' ? (
            <>
              <TouchableOpacity onPress={() => reviewQuestions(item)} style={styles.iconBtn}>
                <Ionicons name="create-outline" size={18} color="#3B82F6" />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => activateChallenge(item.id)} style={[styles.statusBtn, { backgroundColor: '#ECFDF5' }]}>
                <Caption style={{ color: '#059669', fontWeight: 'bold', fontSize: 10 }}>PUBLISH</Caption>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <TouchableOpacity onPress={() => viewAttempts(item)} style={styles.iconBtn}>
                <Ionicons name="bar-chart-outline" size={18} color="#6B7280" />
              </TouchableOpacity>
              {item.status === 'active' && (
                <TouchableOpacity onPress={() => endChallenge(item.id)} style={[styles.statusBtn, { backgroundColor: '#FEF2F2' }]}>
                  <Caption style={{ color: '#EF4444', fontWeight: 'bold', fontSize: 10 }}>STOP</Caption>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <AdminLayout>
      <View style={styles.container}>
        <View style={styles.header}>
          <View>
            <H1 style={styles.pageTitle}>🍯 Mb's Honey Pot</H1>
            <Body1 style={styles.pageSubtitle}>Manage your gamified trivia challenges</Body1>
          </View>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() => { setModalMode('create'); setShowModal(true); }}
          >
            <Ionicons name="add" size={20} color="white" />
            <Body1 style={styles.primaryBtnText}>New Challenge</Body1>
          </TouchableOpacity>
        </View>

        {loading ? (
          <ActivityIndicator size="large" color="#F59E0B" style={{ marginTop: 50 }} />
        ) : (
          <FlatList
            data={challenges}
            keyExtractor={item => item.id.toString()}
            renderItem={renderChallengeItem}
            contentContainerStyle={styles.listContainer}
            ListEmptyComponent={<Body1 style={styles.emptyText}>No challenges found.</Body1>}
          />
        )}
      </View>

      <Modal visible={showModal} animationType="slide" transparent={true}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>

            <View style={styles.modalHeader}>
              <H2 style={styles.modalTitle}>
                {modalMode === 'create' ? 'Create AI Trivia Challenge' :
                  `Review Questions: ${selectedChallenge?.title}`}
              </H2>
              <TouchableOpacity onPress={() => setShowModal(false)} style={styles.closeBtn}>
                <Ionicons name="close" size={24} color="#4B5563" />
              </TouchableOpacity>
            </View>

            {/* CREATE MODE */}
            {modalMode === 'create' && (
              <ScrollView style={styles.formContainer} showsVerticalScrollIndicator={false}>
                <View style={styles.inputGroup}>
                  <Caption style={styles.label}>Challenge Title</Caption>
                  <TextInput style={styles.input} value={formData.title} onChangeText={(t) => setFormData({ ...formData, title: t })} placeholder="e.g. Science Buzz" />
                </View>

                <View style={styles.inputGroup}>
                  <Caption style={styles.label}>Trivia Topic (Prompt for AI)</Caption>
                  <TextInput style={styles.input} value={formData.topic} onChangeText={(t) => setFormData({ ...formData, topic: t })} placeholder="e.g. History of the Internet" />
                </View>

                <View style={styles.row}>
                  <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
                    <Caption style={styles.label}>Number of Questions</Caption>
                    <TextInput style={styles.input} keyboardType="numeric" value={formData.questionCount} onChangeText={(t) => setFormData({ ...formData, questionCount: t })} />
                  </View>
                  <View style={[styles.inputGroup, { flex: 1 }]}>
                    <Caption style={styles.label}>Difficulty</Caption>
                    <TextInput style={styles.input} value={formData.difficulty} onChangeText={(t) => setFormData({ ...formData, difficulty: t })} placeholder="easy, medium, hard" />
                  </View>
                </View>

                <View style={styles.row}>
                  <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
                    <Caption style={styles.label}>Passing Score (%)</Caption>
                    <TextInput style={styles.input} keyboardType="numeric" value={formData.passingScore} onChangeText={(t) => setFormData({ ...formData, passingScore: t })} />
                  </View>
                  <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
                    <Caption style={styles.label}>Quiz Timelimit (min)</Caption>
                    <TextInput style={styles.input} keyboardType="numeric" value={formData.durationMinutes} onChangeText={(t) => setFormData({ ...formData, durationMinutes: t })} />
                  </View>
                  <View style={[styles.inputGroup, { flex: 1 }]}>
                    <Caption style={styles.label}>Expires in (hrs)</Caption>
                    <TextInput style={styles.input} keyboardType="numeric" value={formData.lifetimeHours} onChangeText={(t) => setFormData({ ...formData, lifetimeHours: t })} />
                  </View>
                </View>

                <View style={styles.inputGroup}>
                  <Caption style={styles.label}>Reward Type</Caption>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 5 }}>
                    {[
                      { value: 'mbs', emoji: '📦', name: 'MBs' },
                      { value: 'cash', emoji: '💵', name: 'Cash' },
                      { value: 'points', emoji: '⭐', name: 'Points' },
                      { value: 'fun', emoji: '🎮', name: 'Fun' }
                    ].map(({ value, emoji, name }) => (
                      <TouchableOpacity
                        key={value}
                        onPress={() => setFormData({ ...formData, rewardType: value })}
                        style={{
                          flexDirection: 'row', alignItems: 'center',
                          paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10,
                          backgroundColor: formData.rewardType === value ? '#F59E0B' : '#F3F4F6',
                          borderWidth: 1.5, borderColor: formData.rewardType === value ? '#D97706' : '#E5E7EB'
                        }}
                      >
                        <Text style={{ fontSize: 13, marginRight: 4 }}>{emoji}</Text>
                        <Caption style={{ color: formData.rewardType === value ? 'white' : '#4B5563', fontWeight: '700' }}>
                          {name}
                        </Caption>
                      </TouchableOpacity>
                    ))}
                  </View>
                  {formData.rewardType === 'fun' && (
                    <View style={{ marginTop: 8, padding: 10, backgroundColor: '#F5F3FF', borderRadius: 8, borderLeftWidth: 3, borderLeftColor: '#7C3AED' }}>
                      <Caption style={{ color: '#5B21B6', fontWeight: '600' }}>
                        🎮 Fun Mode: Winners get a mini trivia bonus challenge instead of a wallet reward. No transaction is created.
                      </Caption>
                    </View>
                  )}
                </View>

                <View style={styles.row}>
                  <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
                    <Caption style={styles.label}>
                      {formData.rewardType === 'points' ? 'Points Amount' :
                        formData.rewardType === 'fun' ? 'Fun Config Amount' :
                          'Reward Value (Amount)'}
                    </Caption>
                    <TextInput
                      style={styles.input}
                      keyboardType="numeric"
                      value={formData.bundleRewardAmount}
                      onChangeText={(t) => setFormData({ ...formData, bundleRewardAmount: t })}
                      editable={formData.rewardType !== 'fun'}
                      placeholder={formData.rewardType === 'fun' ? 'N/A' : '50'}
                    />
                  </View>
                  <View style={[styles.inputGroup, { flex: 2 }]}>
                    <Caption style={styles.label}>Reward Label / Description</Caption>
                    <TextInput
                      style={styles.input}
                      value={formData.bundleRewardLabel}
                      onChangeText={(t) => setFormData({ ...formData, bundleRewardLabel: t })}
                      placeholder={formData.rewardType === 'fun' ? 'e.g. Bonus Trivia Round' : 'e.g. 100MB Data or 50 Credits'}
                    />
                  </View>
                </View>

                <TouchableOpacity style={[styles.primaryBtn, { marginTop: 10, width: '100%', justifyContent: 'center' }]} onPress={handleCreate} disabled={loading}>
                  {loading ? <ActivityIndicator color="white" /> : <Body1 style={styles.primaryBtnText}>Generate Trivia & Save</Body1>}
                </TouchableOpacity>
              </ScrollView>
            )}

            {/* QUESTIONS REVIEW MODE */}
            {modalMode === 'questions' && (
              <View style={{ flex: 1 }}>
                {questionsLoading ? (
                  <ActivityIndicator size="large" color="#3B82F6" style={{ marginTop: 50 }} />
                ) : (
                  <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
                    <Body2 style={{ color: '#6B7280', marginBottom: 15 }}>
                      You can modify AI-generated content before publishing. Edit options as comma-separated values.
                    </Body2>
                    {questions.map((q, idx) => (
                      <View key={idx} style={styles.qReviewCard}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Body1 style={{ fontWeight: '700', marginBottom: 8, color: '#374151' }}>Question {idx + 1}</Body1>
                        </View>

                        <Caption style={styles.labelCompact}>Question Text</Caption>
                        <TextInput style={styles.inputCompact} value={q.questionText} onChangeText={(t) => {
                          const newQ = [...questions]; newQ[idx].questionText = t; setQuestions(newQ);
                        }} multiline />

                        <Caption style={styles.labelCompact}>Options (comma separated)</Caption>
                        <TextInput style={styles.inputCompact} value={(q.options || []).join(', ')} onChangeText={(t) => {
                          const newQ = [...questions]; newQ[idx].options = t.split(',').map(s => s.trim()); setQuestions(newQ);
                        }} multiline />

                        <Caption style={styles.labelCompact}>Correct Answer</Caption>
                        <TextInput style={styles.inputCompact} value={q.correctAnswer} onChangeText={(t) => {
                          const newQ = [...questions]; newQ[idx].correctAnswer = t; setQuestions(newQ);
                        }} />

                        <Caption style={styles.labelCompact}>Explanation</Caption>
                        <TextInput style={styles.inputCompact} value={q.explanation} onChangeText={(t) => {
                          const newQ = [...questions]; newQ[idx].explanation = t; setQuestions(newQ);
                        }} multiline />
                      </View>
                    ))}

                    <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: '#10B981', marginTop: 10, width: '100%', justifyContent: 'center' }]} onPress={saveQuestions} disabled={questionsLoading}>
                      {questionsLoading ? <ActivityIndicator color="white" /> : <Body1 style={styles.primaryBtnText}>Save Questions</Body1>}
                    </TouchableOpacity>
                    <View style={{ height: 20 }} />
                  </ScrollView>
                )}
              </View>
            )}

            {/* ATTEMPTS MODE */}
            {modalMode === 'attempts' && (
              <View style={{ flex: 1 }}>
                <View style={styles.modalHeader}>
                  <View>
                    <H2 style={styles.modalTitle}>Detailed Attempts: {selectedChallenge?.title}</H2>
                    <Caption style={{ color: '#6B7280' }}>{attempts.length} total participants</Caption>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 10 }}>
                    <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: '#10B981' }]} onPress={handleBulkReward}>
                      <Ionicons name="flash" size={16} color="white" />
                      <Body1 style={{ color: 'white', fontWeight: 'bold', marginLeft: 5 }}>Bulk Reward Passers</Body1>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setShowModal(false)} style={styles.closeBtn}>
                      <Ionicons name="close" size={24} color="#4B5563" />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Search / Filter */}
                <View style={{ marginBottom: 15 }}>
                  <TextInput
                    style={styles.input}
                    placeholder="Search by name, email, or FCP..."
                    value={attemptSearch}
                    onChangeText={setAttemptSearch}
                  />
                </View>

                {attemptsLoading ? (
                  <ActivityIndicator size="large" color="#F59E0B" />
                ) : (
                  <FlatList
                    data={attempts.filter(a => {
                      const q = attemptSearch.toLowerCase();
                      const u = a.user || {};
                      const p = u.participant || {};
                      const f = p.fcp || {};
                      return u.name?.toLowerCase().includes(q) ||
                        u.email?.toLowerCase().includes(q) ||
                        f.name?.toLowerCase().includes(q);
                    })}
                    keyExtractor={item => item.id.toString()}
                    renderItem={({ item }) => (
                      <View style={styles.analyticsRow}>
                        <View style={{ flex: 2 }}>
                          <Body1 style={{ fontWeight: '700', color: '#1F2937' }}>{item.user?.name}</Body1>
                          <Caption style={{ color: '#6B7280' }}>{item.user?.email}</Caption>
                          <Caption style={{ color: '#F59E0B', fontWeight: 'bold' }}>{item.user?.role?.toUpperCase()}</Caption>
                        </View>

                        <View style={{ flex: 2 }}>
                          <Caption style={{ fontWeight: '600' }}>📱 {item.user?.phone || 'No phone'}</Caption>
                          <Caption style={{ color: '#4B5563' }}>🏢 {item.user?.participant?.fcp?.name || 'N/A'}</Caption>
                          <Caption style={{ color: '#9CA3AF' }}>📍 {item.user?.participant?.fcp?.cluster?.name || 'N/A'}</Caption>
                        </View>

                        <View style={{ flex: 1, alignItems: 'center' }}>
                          <H2 style={{ color: item.passed ? '#059669' : '#DC2626' }}>{item.score}%</H2>
                          <Caption style={{ color: item.passed ? '#059669' : '#DC2626', fontWeight: 'bold' }}>
                            {item.passed ? 'PASSED' : 'FAILED'}
                          </Caption>
                        </View>

                        <View style={{ flex: 1.5, alignItems: 'center' }}>
                          {item.bundleAwarded ? (
                            <View style={{ backgroundColor: '#FEF3C7', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 }}>
                              <Caption style={{ color: '#D97706', fontWeight: 'bold' }}>GIFTED 🎁</Caption>
                            </View>
                          ) : (
                            item.passed ? (
                              <TouchableOpacity
                                onPress={() => handleManualReward(item.id)}
                                style={{ backgroundColor: '#3B82F6', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8 }}
                              >
                                <Caption style={{ color: 'white', fontWeight: 'bold' }}>REWARD NOW</Caption>
                              </TouchableOpacity>
                            ) : (
                              <Caption style={{ color: '#9CA3AF' }}>No Reward</Caption>
                            )
                          )}
                        </View>
                      </View>
                    )}
                  />
                )}
              </View>
            )}

          </View>
        </View>
      </Modal>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, padding: 24, backgroundColor: '#F9FAFB' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 },
  pageTitle: { color: '#111827', marginBottom: 4 },
  pageSubtitle: { color: '#4B5563' },

  primaryBtn: { flexDirection: 'row', backgroundColor: '#F59E0B', paddingHorizontal: 16, paddingVertical: 12, borderRadius: 10, alignItems: 'center', shadowColor: '#D97706', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 4, elevation: 3 },
  primaryBtnText: { color: 'white', fontWeight: '700', marginLeft: 6 },

  listContainer: { paddingBottom: 40 },

  emptyText: { textAlign: 'center', color: '#9CA3AF', marginTop: 40 },

  // Compact Minimalist Cards
  compactCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    flexWrap: 'nowrap'
  },
  cardInfo: {
    flex: 1,
    marginRight: 10
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8
  },
  compactTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1F2937'
  },
  compactMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4
  },
  metaLabel: {
    color: '#6B7280',
    fontSize: 11
  },
  metaDivider: {
    color: '#D1D5DB',
    marginHorizontal: 4,
    fontSize: 10
  },
  compactActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F9FAFB',
    justifyContent: 'center',
    alignItems: 'center'
  },
  statusBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginLeft: 4
  },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(17, 24, 39, 0.6)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { width: '92%', maxWidth: 700, maxHeight: '85%', backgroundColor: 'white', borderRadius: 20, padding: 24, shadowColor: '#000', shadowOffset: { width: 0, height: 10 }, shadowOpacity: 0.15, shadowRadius: 20, elevation: 10 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle: { color: '#111827' },
  closeBtn: { padding: 4, backgroundColor: '#F3F4F6', borderRadius: 20 },

  formContainer: { flex: 1 },
  row: { flexDirection: 'row', justifyContent: 'space-between' },
  inputGroup: { marginBottom: 18 },
  label: { fontWeight: '600', color: '#4B5563', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, backgroundColor: '#F9FAFB', color: '#1F2937' },

  // Specific to review questions mode
  qReviewCard: { backgroundColor: '#F9FAFB', padding: 16, borderRadius: 12, marginBottom: 16, borderWidth: 1, borderColor: '#E5E7EB' },
  labelCompact: { fontWeight: '600', color: '#6B7280', marginBottom: 4, marginTop: 10 },
  inputCompact: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8, fontSize: 14, backgroundColor: 'white' },
  reviewCorrect: { borderColor: '#10B981', backgroundColor: '#ECFDF5' },
  reviewWrong: { borderColor: '#EF4444', backgroundColor: '#FEF2F2' },

  analyticsRow: {
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white'
  }
});

export default HoneyPotManagementScreen;
